import { iaRa } from 'lib/helpers/utility';
import { formatDate } from 'lib/helpers/dateUtils';
import numOrDefault from 'lib/helpers/numOrDefault';

import { COLUMN_NAME, NEGATIVE_COLUMNS, OPTIONAL_KEYS } from './invoiceDocColumns';
import {
  NA,
  cellValue,
  dxCodes,
  itemAmounts,
  money,
  negCell,
  serviceMeta,
  serviceTitle,
  tierAmounts,
  tierChildren
} from './invoiceDocModel';

export const SECTION_COPY = {
  confirmed: {
    title: 'Confirmed balances',
    subtitle: 'These balances are finalized and confirmed by your insurance company.',
    totalsLabel: 'Totals',
    combinedTotalsLabel: 'Confirmed totals'
  },
  gfe: {
    title: 'Good Faith Estimates',
    subtitle:
      'This section outlines all appointments associated with your Good Faith Estimate (GFE), including both past and future appointments.',
    totalsLabel: 'Estimated totals',
    combinedTotalsLabel: 'Estimated totals'
  }
};

const GFE_BANDS = {
  past: { heading: 'Past appointments' },
  future: {
    heading: 'Future appointments',
    note: 'This invoice includes charges for upcoming appointments. Payments made now will be applied toward those visits and adjusted if your schedule or coverage changes.'
  }
};

const buildRow = ({ item, keys, claims, timezone, index, hidden }) => {
  const amounts = itemAmounts(item, { claims });

  return {
    key: `row-${item?.id ?? index}`,
    titleSuffix: hidden?.has('icd_10_codes') ? '' : dxCodes(item),
    subLines: [serviceMeta(item, timezone)].filter(Boolean),
    cells: [
      { value: serviceTitle(item, hidden), align: 'left', strong: true, tabular: false },
      ...keys.map((key) => ({ value: cellValue(key, amounts[key]), align: 'right' }))
    ]
  };
};

const buildTierRows = ({ item, keys, index }) =>
  tierChildren(item)
    .filter(({ child }) => child)
    .map(({ label, child }, tierIndex) => {
      const amounts = tierAmounts(child);

      return {
        key: `row-${item?.id ?? index}-tier-${tierIndex}`,
        tint: true,
        cells: [
          { value: label, align: 'left', tabular: false },
          ...keys.map((key) => ({ value: cellValue(key, amounts[key]), align: 'right' }))
        ]
      };
    });

const expandRows = ({ rows, keys, claims, timezone, hidden }) =>
  iaRa(rows).flatMap((item, index) => {
    const tiers = buildTierRows({ item, keys, index });
    const line = buildRow({ item, keys, claims, timezone, index, hidden });

    return tiers.length > 1 ? [line, ...tiers] : [line];
  });

export const buildTotals = ({ rows, keys, claims, label }) => {
  const sums = {};
  keys.forEach((key) => {
    sums[key] = 0;
  });

  iaRa(rows).forEach((item) => {
    const amounts = itemAmounts(item, { claims });
    keys.forEach((key) => {
      sums[key] += numOrDefault(amounts[key]);
    });
  });

  return [
    { value: label, align: 'left', tabular: false },
    ...keys.map((key) => ({
      value: NEGATIVE_COLUMNS.has(key) ? negCell(sums[key]) : money(sums[key]),
      align: 'right'
    }))
  ];
};

export const isGfeRow = (item, fallbackStatus) =>
  (item?.invoiceStatus || fallbackStatus || 'Confirmed') !== 'Confirmed';

const dayKey = (value, timezone) => formatDate(value, timezone, 'yyyy-MM-dd');

const isPast = (item, timezone) => {
  if (!item?.dateofservice) return true;
  return dayKey(item.dateofservice, timezone) < dayKey(new Date(), timezone);
};

const gfeGroups = ({ rows, keys, claims, timezone, hidden }) =>
  [
    { band: GFE_BANDS.past, rows: rows.filter((item) => isPast(item, timezone)) },
    { band: GFE_BANDS.future, rows: rows.filter((item) => !isPast(item, timezone)) }
  ]
    .filter((group) => group.rows.length)
    .map((group, index) => ({
      key: `gfe-${index}`,
      heading: group.band.heading,
      note: group.band.note,
      rows: expandRows({ rows: group.rows, keys, claims, timezone, hidden })
    }));

/**
 * @returns {Array<{key: string, copy: object, totalsLabel: string, groups: Array}>}
 */
export const buildSections = ({ rows, keys, claims, timezone, columns, status, hidden }) => {
  const confirmed = iaRa(rows).filter((item) => !isGfeRow(item, status));
  const gfe = iaRa(rows).filter((item) => isGfeRow(item, status));
  const combined = !!confirmed.length && !!gfe.length;

  const sections = [];

  if (confirmed.length) {
    const copy = SECTION_COPY.confirmed;
    sections.push({
      key: 'confirmed',
      copy,
      table: {
        columns,
        strongTotals: true,
        groups: [
          {
            key: 'confirmed',
            rows: expandRows({ rows: confirmed, keys, claims, timezone, hidden })
          }
        ],
        totals: buildTotals({
          rows: confirmed,
          keys,
          claims,
          label: combined ? copy.combinedTotalsLabel : copy.totalsLabel
        })
      }
    });
  }

  if (gfe.length) {
    const copy = SECTION_COPY.gfe;
    sections.push({
      key: 'gfe',
      copy,
      table: {
        columns,
        strongTotals: true,
        groups: gfeGroups({ rows: gfe, keys, claims, timezone, hidden }),
        totals: buildTotals({ rows: gfe, keys, claims, label: copy.totalsLabel })
      }
    });
  }

  return sections;
};

const GLOSSARY = [
  'DOS = date of service',
  'Ins. = insurance',
  'Pt. = patient',
  'Adj. = contractual adjustment (charge less allowed)',
  'Allowed = amount approved by your plan',
  `${NA} = not applicable`
];

export const buildLegend = (keys) => {
  const segments = [...GLOSSARY];

  if (keys.includes('disc_wo')) segments.push('Disc./W-O = discount or write-off');

  const missing = OPTIONAL_KEYS.filter((key) => !keys.includes(key)).map((key) => COLUMN_NAME[key]);

  const glossary = `${segments.join(' · ')}.`;

  return missing.length
    ? `${glossary} Not shown (no activity on this invoice): ${missing.join(', ')}.`
    : glossary;
};
